import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { FileNote, LoanFile } from '../types/goals';
import { MessageSquare, Clock, AlertCircle, Send, DollarSign, MapPin } from 'lucide-react';
import { Badge } from './ui/Badge';
interface FileNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: LoanFile | null;
  notes: FileNote[];
  onAddNote: (content: string, isUrgent: boolean) => void;
  userRole?: 'manager' | 'processor';
}
export function FileNotesModal({
  isOpen,
  onClose,
  file,
  notes,
  onAddNote,
  userRole = 'manager'
}: FileNotesModalProps) {
  const [newNote, setNewNote] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const handleSubmit = () => {
    if (newNote.trim()) {
      onAddNote(newNote.trim(), isUrgent);
      setNewNote('');
      setIsUrgent(false);
    }
  };
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const sortedNotes = [...notes].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  if (!file) return null;
  const noteRecipient = userRole === 'manager' ? 'the processor' : 'your manager';
  const notePlaceholder = userRole === 'manager' ? 'Leave instructions or feedback about this file...' : 'Leave a note or question for your manager about this file...';
  return <Modal isOpen={isOpen} onClose={onClose} title={`File Notes: ${file.borrowerName}`} description={`View recent activity and communicate about this file`} size="lg" data-id="element-43">
      <div className="space-y-6" data-id="element-44">
        {/* File Details Summary */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-45">
          <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-46">
            <div className="flex items-center gap-2 text-slate-600" data-id="element-47">
              <DollarSign className="w-4 h-4 text-slate-400" data-id="element-48" />
              <span className="font-medium" data-id="element-49">
                ${file.loanAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-2 text-slate-600" data-id="element-50">
              <MapPin className="w-4 h-4 text-slate-400" data-id="element-51" />
              <span className="truncate" data-id="element-52">{file.propertyAddress}</span>
            </div>
          </div>
          <div className="mt-2 text-sm text-slate-600" data-id="element-53">
            <span className="font-medium" data-id="element-54">Status:</span> {file.status}
          </div>
          {file.openConditions !== undefined && <div className="mt-1 text-sm text-slate-600" data-id="element-55">
              <span className="font-medium" data-id="element-56">Open Conditions:</span>{' '}
              {file.openConditions}
            </div>}
        </div>

        {/* Add New Note Section */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-57">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-58">
            Add a note for {noteRecipient}
          </label>
          <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder={notePlaceholder} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-59" />
          <div className="flex items-center justify-between mt-3" data-id="element-60">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-id="element-61">
              <input type="checkbox" checked={isUrgent} onChange={e => setIsUrgent(e.target.checked)} className="rounded border-slate-300 text-red-600 focus:ring-red-500" data-id="element-62" />
              <AlertCircle className="w-4 h-4 text-red-600" data-id="element-63" />
              Mark as urgent
            </label>
            <Button size="sm" onClick={handleSubmit} disabled={!newNote.trim()} data-id="element-64">
              <Send className="w-4 h-4 mr-2" data-id="element-65" />
              Post Note
            </Button>
          </div>
        </div>

        {/* Notes List */}
        <div data-id="element-66">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-67">
            <MessageSquare className="w-4 h-4" data-id="element-68" />
            Recent Notes ({sortedNotes.length})
          </h3>

          {sortedNotes.length === 0 ? <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-slate-200" data-id="element-69">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-400" data-id="element-70" />
              <p className="text-sm" data-id="element-71">No notes yet</p>
              <p className="text-xs text-slate-400 mt-1" data-id="element-72">
                Be the first to leave a note on this file
              </p>
            </div> : <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-73">
              {sortedNotes.map(note => <div key={note.id} className={`p-4 rounded-lg border ${note.isUrgent ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-id="element-74">
                  <div className="flex items-start justify-between mb-2" data-id="element-75">
                    <div className="flex items-center gap-2" data-id="element-76">
                      <span className="text-sm font-medium text-slate-900" data-id="element-77">
                        {note.author}
                      </span>
                      <Badge variant={note.authorRole === 'manager' ? 'info' : 'neutral'} className="text-xs" data-id="element-78">
                        {note.authorRole === 'manager' ? 'Manager' : 'Processor'}
                      </Badge>
                      {note.isUrgent && <Badge variant="danger" className="flex items-center gap-1" data-id="element-79">
                          <AlertCircle className="w-3 h-3" data-id="element-80" />
                          Urgent
                        </Badge>}
                    </div>
                    <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-81">
                      <Clock className="w-3 h-3" data-id="element-82" />
                      {formatTimestamp(note.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-id="element-83">
                    {note.content}
                  </p>
                </div>)}
            </div>}
        </div>
      </div>
    </Modal>;
}